/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { Observable, Subject, of, EMPTY, merge } from 'rxjs';
import { tap, takeUntil, mergeMap, take, catchError, switchMap } from 'rxjs/operators';
import { AngularFireDatabase, AngularFireAction, DatabaseSnapshot } from '@angular/fire/compat/database';
import { AngularLandConnection } from './interfaces';
import { FancyActionTrackerFactory } from '../services/fancy-action-tracker-factory.service';
import { DatabaseReference, SnapshotAction } from '@angular/fire/compat/database/interfaces';
import { logger } from '@touchplan/logging';
import { LoggerServiceFactory } from '../../logging/LoggerServiceFactory';

export class Fragment {
	public lastSnap: AngularFireAction<DatabaseSnapshot<any>>;
	public event$: Observable<AngularFireAction<DatabaseSnapshot<any>>>;

	private shutdownSubject = new Subject<void>();
	private finalEvent$ = new Subject<AngularFireAction<DatabaseSnapshot<any>>>();

	constructor(private snapshotAction: Observable<SnapshotAction<DatabaseSnapshot<any>>>) {
		const realEvent$ = snapshotAction.pipe(
			takeUntil(this.shutdownSubject),
			mergeMap((snap) => {
				let abort = false;
				const lastExists = this.lastSnap !== undefined && this.lastSnap.payload.exists();
				//added
				if (!lastExists && snap.payload.exists()) {
					snap.type = 'child_added';
				}
				//removed
				else if (lastExists && !snap.payload.exists()) {
					snap.type = 'child_removed';
				}
				//no-op, is silly
				else if (!lastExists && !snap.payload.exists()) {
					abort = true;
				}
				//changed
				else {
					snap.type = 'child_changed';
				}
				this.lastSnap = snap;
				if (abort) {
					return EMPTY;
				}
				return of(snap);
			}),
			catchError((e, val) => {
				logger.log('a projectMember index points to a project you do not have access to', e, val);
				//an error essentially means no access, so don't emit a result (this will likely break if a user gains or loses access during a session)
				return EMPTY;
			})
		);

		this.event$ = merge(realEvent$, this.finalEvent$);
	}

	public stop() {
		if (this.lastSnap.type !== 'child_removed') {
			this.lastSnap.type = 'child_removed';
			this.finalEvent$.next(this.lastSnap);
		}
		this.shutdownSubject.next();
	}
}

type RefOrRefGetter = DatabaseReference | ((childId: string) => Observable<DatabaseReference>);
function isRef(ref: RefOrRefGetter): ref is DatabaseReference {
	return (ref as any).key;
}

export enum RefSourceType {
	StaticRef,
	ObservableRef,
}

/**
 * FancyFragmenter exists as a class that can request a partial list from firebase.
 * It works by accepting the location of the whole list, and waiting for add/remove
 * calls to indicate what is included in the list. It then makes object calls
 * via angularfire and transforms them into list calls. The net result being
 * an observable that emits roughly equivalent events to angularfire2 stateEvents.
 * FancyIdJoin currently implements this api.
 */
export class FancyFragmenter {
	protected list: Map<string, Fragment> = new Map();
	protected angularFireDatabase: AngularFireDatabase;
	protected refSourceType: RefSourceType;

	protected baseRef: DatabaseReference;
	protected refGetter: (childId: string) => Observable<DatabaseReference>;

	private shutdownSubject = new Subject<void>();

	protected innerEmitter$: Subject<AngularFireAction<DatabaseSnapshot<any>>> = new Subject();
	public emitter$: Observable<AngularFireAction<DatabaseSnapshot<any>>> = this.innerEmitter$.pipe(
		takeUntil(this.shutdownSubject)
	);

	constructor(angularLand: AngularLandConnection, refOrGetter: RefOrRefGetter) {
		this.angularFireDatabase = angularLand.angularFireDatabase;
		this.parseInput(refOrGetter);
	}

	protected parseInput(refOrGetter: RefOrRefGetter) {
		if (isRef(refOrGetter)) {
			this.refSourceType = RefSourceType.StaticRef;
			this.baseRef = refOrGetter;
		} else {
			this.refSourceType = RefSourceType.ObservableRef;
			this.refGetter = refOrGetter;
		}
	}

	//isolate out the start to ensure all the firebase lists are listening before we start requesting events
	public start(initialIds?: string[]) {
		if (initialIds) {
			initialIds.forEach((id) => {
				this.add(id);
			});
		}
	}

	public add(id: string) {
		if (!this.list.has(id)) {
			const ob$ = this.getChildRef(id).pipe(
				take(1),
				switchMap((ref) => this.angularFireDatabase.object<DatabaseSnapshot<any>>(ref).snapshotChanges())
			);
			const fragment = new Fragment(ob$);

			fragment.event$
				.pipe(
					tap((thing) => {
						(thing as any).key = id;
					})
				)
				.subscribe(this.innerEmitter$);
			this.list.set(id, fragment);
		}
	}

	public has(id: string) {
		return this.list.has(id);
	}

	public getChildRef(id: string) {
		if (this.refSourceType === RefSourceType.StaticRef) {
			return of(this.baseRef.child(id));
		} else {
			return this.refGetter(id);
		}
	}
	public remove(id: string) {
		const fragment = this.list.get(id);
		if (!fragment) {
			return;
		}

		fragment.stop();
		this.list.delete(id);
	}
	public destroy() {
		this.shutdownSubject.next();
		this.list = null;
	}
}

@Injectable({
	providedIn: 'root',
})
export class FancyFragmenterFactory {
	constructor(
		protected db: AngularFireDatabase,
		protected fancyActionTrackerFactory: FancyActionTrackerFactory
	) {}
	public create(path: DatabaseReference): FancyFragmenter {
		const angularLand = {
			angularFireDatabase: this.db,
			fancyActionTrackerFactory: this.fancyActionTrackerFactory,
		};
		return new FancyFragmenter(angularLand, path);
	}
}
